import React, { useState } from "react";
import { CardComp, TabsComp, ModalCompAdvanceUpdate } from "../../../components";
import PendingRequestsTab from "./modules/withdrawal-requests/PendingRequestsTab";
import ApprovedRequestsTab from "./modules/withdrawal-requests/ApprovedRequestsTab";
import DeclinedRequestsTab from "./modules/withdrawal-requests/DeclinedRequestsTab";
import PaidRequestsTab from "./modules/withdrawal-requests/PaidRequestsTab";
import AllRequestsTab from "./modules/withdrawal-requests/AllRequestsTab";
// ApproveWithdrawalModal is handled by Message component inline
import DeclineWithdrawalModal from "./modules/withdrawal-requests/modal/DeclineWithdrawalModal";
import MarkAsPaidWithdrawalModal from "./modules/withdrawal-requests/modal/MarkAsPaidWithdrawalModal";
import ViewWithdrawalDetailsModal from "./modules/withdrawal-requests/modal/ViewWithdrawalDetailsModal";
import Message from "../../../components/MessageModal";
import {
  useApproveWithdrawalRequestMutation,
  useDeclineWithdrawalRequestMutation,
  useMarkWithdrawalAsPaidMutation,
} from "../../../store/admin/contribution";
import toast from "react-hot-toast";

export default function WithdrawalRequests() {
  const [modalPage, setModalPage] = useState("");
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [pendingPage, setPendingPage] = useState(1);
  const [approvedPage, setApprovedPage] = useState(1);
  const [declinedPage, setDeclinedPage] = useState(1);
  const [paidPage, setPaidPage] = useState(1);
  const [allPage, setAllPage] = useState(1);

  const [approveWithdrawal, { isLoading: isApproving }] =
    useApproveWithdrawalRequestMutation();
  const [declineWithdrawal, { isLoading: isDeclining }] =
    useDeclineWithdrawalRequestMutation();
  const [markAsPaid, { isLoading: isMarkingAsPaid }] =
    useMarkWithdrawalAsPaidMutation();

  const handleApprove = async (requestId) => {
    try {
      const response = await approveWithdrawal(requestId).unwrap();
      if (response.status) {
        toast.success(response.message || "Withdrawal request approved successfully");
        setModalPage("");
        setSelectedRequestId(null);
      } else {
        toast.error(response.message || "Failed to approve withdrawal request");
        setModalPage("");
        setSelectedRequestId(null);
      }
    } catch (error) {
      toast.error(error?.data?.message || error?.message || "Failed to approve withdrawal request");
      setModalPage("");
      setSelectedRequestId(null);
    }
  };

  const handleDecline = async (requestId, declineReason) => {
    try {
      const response = await declineWithdrawal({
        requestId,
        decline_reason: declineReason,
      }).unwrap();
      if (response.status) {
        toast.success(response.message || "Withdrawal request declined successfully");
        setModalPage("");
        setSelectedRequestId(null);
      } else {
        toast.error(response.message || "Failed to decline withdrawal request");
        setModalPage("");
        setSelectedRequestId(null);
      }
    } catch (error) {
      toast.error(error?.data?.message || error?.message || "Failed to decline withdrawal request");
      setModalPage("");
      setSelectedRequestId(null);
    }
  };

  const handleMarkAsPaid = async (requestId, paymentDate, paymentReference) => {
    try {
      const response = await markAsPaid({
        requestId,
        payment_date: paymentDate,
        payment_reference: paymentReference || null,
      }).unwrap();
      if (response.status) {
        toast.success(response.message || "Withdrawal request marked as paid successfully");
        setModalPage("");
        setSelectedRequestId(null);
      } else {
        toast.error(response.message || "Failed to mark withdrawal as paid");
        setModalPage("");
        setSelectedRequestId(null);
      }
    } catch (error) {
      toast.error(error?.data?.message || error?.message || "Failed to mark withdrawal as paid");
      setModalPage("");
      setSelectedRequestId(null);
    }
  };

  const useModal = [
    {
      name: "Approve Modal",
      component: (
        <Message
          title="Confirm Approval"
          loading={isApproving}
          desc="Are you sure you want to approve this withdrawal request?"
          onBack={() => {
            setModalPage("");
            setSelectedRequestId(null);
          }}
          onProceed={() => handleApprove(selectedRequestId)}
        />
      ),
    },
    {
      name: "Decline Modal",
      component: (
        <DeclineWithdrawalModal
          requestId={selectedRequestId}
          onDecline={handleDecline}
          onClose={() => {
            setModalPage("");
            setSelectedRequestId(null);
          }}
          isLoading={isDeclining}
        />
      ),
    },
    {
      name: "Mark as Paid Modal",
      component: (
        <MarkAsPaidWithdrawalModal
          requestId={selectedRequestId}
          onMarkAsPaid={handleMarkAsPaid}
          onClose={() => {
            setModalPage("");
            setSelectedRequestId(null);
          }}
          isLoading={isMarkingAsPaid}
        />
      ),
    },
    {
      name: "View Details Modal",
      component: (
        <ViewWithdrawalDetailsModal
          requestId={selectedRequestId}
          onClose={() => {
            setModalPage("");
            setSelectedRequestId(null);
          }}
        />
      ),
    },
  ];

  const useTab = [
    {
      name: "Pending Requests",
      component: (
        <PendingRequestsTab
          page={pendingPage}
          setPage={setPendingPage}
          onApprove={(requestId) => {
            setSelectedRequestId(requestId);
            setModalPage("Approve Modal");
          }}
          onDecline={(requestId) => {
            setSelectedRequestId(requestId);
            setModalPage("Decline Modal");
          }}
          onViewDetails={(requestId) => {
            setSelectedRequestId(requestId);
            setModalPage("View Details Modal");
          }}
        />
      ),
    },
    {
      name: "Approved Requests",
      component: (
        <ApprovedRequestsTab
          page={approvedPage}
          setPage={setApprovedPage}
          onMarkAsPaid={(requestId) => {
            setSelectedRequestId(requestId);
            setModalPage("Mark as Paid Modal");
          }}
          onViewDetails={(requestId) => {
            setSelectedRequestId(requestId);
            setModalPage("View Details Modal");
          }}
        />
      ),
    },
    {
      name: "Declined Requests",
      component: (
        <DeclinedRequestsTab
          page={declinedPage}
          setPage={setDeclinedPage}
          onViewDetails={(requestId) => {
            setSelectedRequestId(requestId);
            setModalPage("View Details Modal");
          }}
        />
      ),
    },
    {
      name: "Paid Requests",
      component: (
        <PaidRequestsTab
          page={paidPage}
          setPage={setPaidPage}
          onViewDetails={(requestId) => {
            setSelectedRequestId(requestId);
            setModalPage("View Details Modal");
          }}
        />
      ),
    },
    {
      name: "All Requests",
      component: (
        <AllRequestsTab
          page={allPage}
          setPage={setAllPage}
          onViewDetails={(requestId) => {
            setSelectedRequestId(requestId);
            setModalPage("View Details Modal");
          }}
        />
      ),
    },
  ];

  return (
    <section className="px-lg-3">
      <CardComp cardClassName={"border-0 ps-3 mb-3"}>
        <div>
          <h3 className="text-black1 fw-bold mb-0">Savings withdrawals</h3>
          <p className="text-muted mb-0">
            Manage member savings withdrawal requests
          </p>
        </div>
      </CardComp>

      <CardComp cardClassName={"border-0 ps-3"}>
        <div className="py-4"></div>
        <TabsComp
          TabArray={useTab}
          input="name"
          justifyContent={"justify-content-start"}
          tabClassName="px-0"
          TabArrayClassName="px-4"
        />
      </CardComp>

      <ModalCompAdvanceUpdate
        pageName={modalPage}
        arrayComp={useModal}
        handleClose={() => {
          setModalPage("");
          setSelectedRequestId(null);
        }}
        centered
        size={modalPage === "View Details Modal" ? "xl" : "lg"}
      />
    </section>
  );
}

